import React, { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi';
import { useUser } from '../hooks/useUser';
import { sharedStyles } from '../styles/sharedStyles';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import ExcelJS from 'exceljs';
import { useLocation } from 'react-router-dom';
import { formatDateTimeForDisplay, formatDateForDisplay } from '../utils/timezone';
import SearchableDropdown from '../components/SearchableDropdown';

// Helper to get the correct rate and amount for ticket items
const getTicketItemRateAndAmount = (ticketItem) => {
  const machine = ticketItem.Machine || {};
  if (ticketItem.rateType === 'daily') {
    const rate = parseFloat(machine.dayRate) || 0;
    return { rate, rateLabel: 'per day', amount: rate };
  }
  if (ticketItem.rateType === 'load') {
    const rate = parseFloat(machine.loadRate) || 0;
    const loads = parseFloat(ticketItem.loadsUsed) || 0;
    return { rate, rateLabel: 'per load', amount: loads * rate };
  }
  // hourly (default)
  {
    const hours = parseFloat(ticketItem.hoursUsed) || 0;
    const rate = parseFloat(ticketItem.hourlyRate) || 0;
    return { rate, rateLabel: 'per hour', amount: hours * rate };
  }
};

// Helper to format hours based on rate type
const formatHours = (qty, rateType) => {
  if (rateType === 'daily') {
    return 'Daily Rate';
  }
  if (rateType === 'load') {
    const loads = parseFloat(qty || 0);
    return loads === 0 ? '0 loads' : `${loads.toFixed(0)} loads`;
  }
  const hours = parseFloat(qty || 0);
  return hours === 0 ? '0 hrs' : `${hours.toFixed(1)} hrs`;
};

const sumTicketItemsAmount = (items) =>
  (items || []).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);

const sumTicketItemsGallons = (items) =>
  (items || []).reduce((s, i) => s + (parseFloat(i.gallonsUsed) || 0), 0);

/** Matches Tickets list: prefer hourly totals, then loads, then daily. Uses only the given line items (e.g. machine-filtered). */
const formatTicketItemsHoursTotal = (items) => {
  if (!items?.length) return 'N/A';
  const hourlyItems = items.filter((i) => i.rateType === 'hourly');
  const loadItems = items.filter((i) => i.rateType === 'load');
  const dailyItems = items.filter((i) => i.rateType === 'daily');
  if (hourlyItems.length > 0) {
    const totalHours = hourlyItems.reduce((sum, i) => sum + parseFloat(i.hoursUsed || 0), 0);
    return formatHours(totalHours, 'hourly');
  }
  if (loadItems.length > 0) {
    const totalLoads = loadItems.reduce((sum, i) => sum + parseFloat(i.loadsUsed || 0), 0);
    return formatHours(totalLoads, 'load');
  }
  if (dailyItems.length > 0) return formatHours(null, 'daily');
  return 'N/A';
};

/** Numeric total for Excel/CSV footer (hours + loads + 1 per daily line). */
const sumReportItemsQuantity = (items) =>
  (items || []).reduce((sum, item) => {
    if (item.rateType === 'hourly') return sum + (parseFloat(item.hoursUsed) || 0);
    if (item.rateType === 'load') return sum + (parseFloat(item.loadsUsed) || 0);
    if (item.rateType === 'daily') return sum + 1;
    return sum + (parseFloat(item.hoursUsed) || 0);
  }, 0);

// Print view component
const PrintView = ({ reportData }) => {
  const formatCurrency = (amount) => {
    if (isNaN(amount) || amount === null || amount === undefined) {
      return '$0';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return formatDateForDisplay(dateStr);
  };

  const renderReport = () => {
    return `
      <div style="font-family: Arial, sans-serif; max-width: 1200px; margin: 0 auto; padding: 20px;">
        <div style="display: flex; align-items: center; margin-bottom: 30px;">
          <div style="flex: 0 0 auto; margin-right: 20px;">
            <img src="/thumbnail_GLW Logo (new).png" alt="GLW Logo" style="height: 150px; width: auto; max-width: 450px;">
          </div>
          <div style="flex: 1; text-align: center;">
            <h1 style="color: #333; margin-bottom: 10px;">Tickets Report</h1>
            <p style="color: #666; margin: 0;">Generated on ${formatDateTimeForDisplay(new Date())}</p>
          </div>
        </div>

        ${Object.keys(reportData.filters).length > 0 ? `
          <div style="margin-bottom: 20px;">
            <h3 style="font-size: 16px; color: #333; margin-bottom: 10px;">Active Filters</h3>
            <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 10px;">
              ${reportData.filters.customerId ? `
                <div><strong>Customer:</strong> ${reportData.filters.customerName}</div>
              ` : ''}
              ${reportData.filters.jobId ? `
                                        <div><strong>Job Name:</strong> ${reportData.filters.jobTitle}</div>
              ` : ''}
              ${reportData.filters.status ? `
                <div><strong>Status:</strong> ${reportData.filters.status}</div>
              ` : ''}
              ${reportData.filters.startDate ? `
                <div><strong>Created From:</strong> ${formatDate(reportData.filters.startDate)}</div>
              ` : ''}
              ${reportData.filters.endDate ? `
                <div><strong>Created To:</strong> ${formatDate(reportData.filters.endDate)}</div>
              ` : ''}
              ${reportData.filters.ticketNumber ? `
                <div><strong>Ticket Number:</strong> ${reportData.filters.ticketNumber}</div>
              ` : ''}
            </div>
          </div>
        ` : ''}

        <!-- Summary -->
        <div style="display: grid; grid-template-columns: repeat(5, 1fr); gap: 15px; margin-bottom: 20px;">
          <div style="border: 1px solid #e5e9f0; padding: 15px; border-radius: 8px;">
            <div style="color: #666; font-size: 14px;">Total Tickets</div>
            <div style="font-size: 24px; font-weight: bold;">${reportData.summary.totalTickets}</div>
          </div>
          <div style="border: 1px solid #e5e9f0; padding: 15px; border-radius: 8px;">
            <div style="color: #666; font-size: 14px;">Total Amount</div>
            <div style="font-size: 24px; font-weight: bold;">${formatCurrency(reportData.summary.totalAmount)}</div>
          </div>
          <div style="border: 1px solid #e5e9f0; padding: 15px; border-radius: 8px;">
            <div style="color: #666; font-size: 14px;">Total Hours</div>
            <div style="font-size: 24px; font-weight: bold;">${reportData.summary.totalHoursDisplay ?? reportData.summary.totalHours.toFixed(1)}</div>
          </div>
          <div style="border: 1px solid #e5e9f0; padding: 15px; border-radius: 8px;">
            <div style="color: #666; font-size: 14px;">Total Gallons</div>
            <div style="font-size: 24px; font-weight: bold;">${reportData.summary.totalGallons ? reportData.summary.totalGallons.toFixed(2) : '0.00'}</div>
          </div>
          <div style="border: 1px solid #e5e9f0; padding: 15px; border-radius: 8px;">
            <div style="color: #666; font-size: 14px;">Pending Tickets</div>
            <div style="font-size: 24px; font-weight: bold;">${reportData.summary.pendingTickets}</div>
          </div>
        </div>

        <!-- Table -->
        <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
          <thead>
            <tr style="background-color: #f8f9fa;">
              <th style="padding: 12px; border: 1px solid #e5e9f0; text-align: left;">Ticket #</th>
              <th style="padding: 12px; border: 1px solid #e5e9f0; text-align: left;">Customer</th>
                                      <th style="padding: 12px; border: 1px solid #e5e9f0; text-align: left;">Job Name</th>
              <th style="padding: 12px; border: 1px solid #e5e9f0; text-align: left;">Work Date</th>
              <th style="padding: 12px; border: 1px solid #e5e9f0; text-align: left;">Creator</th>
              <th style="padding: 12px; border: 1px solid #e5e9f0; text-align: left;">Machines</th>
              <th style="padding: 12px; border: 1px solid #e5e9f0; text-align: right;">Hours</th>
              <th style="padding: 12px; border: 1px solid #e5e9f0; text-align: right;">Gallons Used</th>
              <th style="padding: 12px; border: 1px solid #e5e9f0; text-align: right;">Amount</th>
              <th style="padding: 12px; border: 1px solid #e5e9f0; text-align: center;">Status</th>
            </tr>
          </thead>
          <tbody>
            ${reportData.tickets.map(ticket => `
              <tr>
                <td style="padding: 12px; border: 1px solid #e5e9f0;">
                  <div style="font-weight: 500;">${ticket.ticketNumber}</div>
                  <div style="font-size: 12px; color: #666;">Created: ${formatDate(ticket.createdAt)}</div>
                </td>
                <td style="padding: 12px; border: 1px solid #e5e9f0;">${ticket.Customer?.companyName || 'N/A'}</td>
                <td style="padding: 12px; border: 1px solid #e5e9f0;">${ticket.Job?.title || 'N/A'}</td>
                <td style="padding: 12px; border: 1px solid #e5e9f0;">${ticket.workDate ? formatDate(ticket.workDate) : 'N/A'}</td>
                <td style="padding: 12px; border: 1px solid #e5e9f0;">${ticket.createdByName || 'N/A'}</td>
                <td style="padding: 12px; border: 1px solid #e5e9f0;">
                  ${ticket.TicketItems?.map(item => {
                    const gallons = parseFloat(item.gallonsUsed) || 0;
                    return `${item.Machine?.name || 'N/A'} (${formatHours(item.rateType === 'load' ? item.loadsUsed : item.hoursUsed, item.rateType)})${gallons > 0 ? ` - ${gallons.toFixed(2)} gal` : ''}${item.Employee ? ` - Op: ${item.Employee.name}` : ''}`;
                  }).join(', ') || 'N/A'}
                </td>
                <td style="padding: 12px; border: 1px solid #e5e9f0; text-align: right;">${formatTicketItemsHoursTotal(ticket.TicketItems)}</td>
                <td style="padding: 12px; border: 1px solid #e5e9f0; text-align: right;">${(() => {
                  const total = sumTicketItemsGallons(ticket.TicketItems);
                  return total > 0 ? `${total.toFixed(2)} gal` : '0.00';
                })()}</td>
                <td style="padding: 12px; border: 1px solid #e5e9f0; text-align: right;">${formatCurrency(sumTicketItemsAmount(ticket.TicketItems))}</td>
                <td style="padding: 12px; border: 1px solid #e5e9f0; text-align: center;">
                  <span style="
                    padding: 4px 8px;
                    border-radius: 4px;
                    background-color: ${ticket.status === 'Paid' ? '#e8f5e9' : 
                                     ticket.status === 'Pending' ? '#fff3e0' :
                                     ticket.status === 'Rejected' ? '#ffebee' : '#e3f2fd'};
                    color: ${ticket.status === 'Paid' ? '#2e7d32' :
                           ticket.status === 'Pending' ? '#f57c00' :
                           ticket.status === 'Rejected' ? '#d32f2f' : '#1976d2'};
                    font-size: 12px;
                    font-weight: 500;
                  ">
                    ${ticket.status}
                  </span>
                </td>
              </tr>
            `).join('')}
          </tbody>
          <tfoot>
            <tr style="background-color: #f8f9fa;">
              <td colSpan="7" style="padding: 12px; border: 1px solid #e5e9f0; text-align: right; font-weight: bold;">
                Total:
              </td>
              <td style="padding: 12px; border: 1px solid #e5e9f0; text-align: right; font-weight: bold;">
                ${reportData.summary.totalHoursDisplay ?? `${reportData.summary.totalHours.toFixed(1)} hrs`}
              </td>
              <td style="padding: 12px; border: 1px solid #e5e9f0; text-align: right; font-weight: bold;">
                ${reportData.summary.totalGallons ? reportData.summary.totalGallons.toFixed(2) : '0.00'} gal
              </td>
              <td style="padding: 12px; border: 1px solid #e5e9f0; text-align: right; font-weight: bold;">
                ${formatCurrency(reportData.summary.totalAmount)}
              </td>
              <td style="padding: 12px; border: 1px solid #e5e9f0;"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    `;
  };

  return (
    <div dangerouslySetInnerHTML={{ __html: renderReport() }} />
  );
};

const TicketReports = () => {
  const api = useApi();
  const { user, loading: userLoading } = useUser();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tickets, setTickets] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [machines, setMachines] = useState([]);
  const [filters, setFilters] = useState({
    customerId: '',
    jobId: '',
    status: '',
    startDate: '',
    endDate: '',
    ticketNumber: '',
    machineId: ''
  });

  // Check if there are any tickets at all (for guidance)
  const [hasAnyTickets, setHasAnyTickets] = useState(null);

  // Handle URL parameters on component mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const ticketNumber = searchParams.get('ticketNumber');
    
    if (ticketNumber) {
      setFilters(prev => ({
        ...prev,
        ticketNumber: ticketNumber
      }));
    }
  }, [location.search]);

  // Fetch initial data for dropdowns
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch customers for dropdown
        const customersResponse = await api({
          method: 'get',
          url: '/customers/dropdown'
        });
        if (customersResponse.data.success) {
          setCustomers(customersResponse.data.data);
        }

        // Fetch jobs for dropdown
        const jobsResponse = await api({
          method: 'get',
          url: '/jobs/dropdown'
        });
        if (jobsResponse.data.success) {
          setJobs(jobsResponse.data.data);
        }

        // Fetch machines
        const machinesResponse = await api({
          method: 'get',
          url: '/machines',
          params: { limit: 1000 }
        });
        if (machinesResponse.data.success) {
          setMachines(machinesResponse.data.data.machines || machinesResponse.data.data);
        }
      } catch (err) {
        console.error('Error fetching initial data:', err);
        setError('Error loading initial data. Please try again.');
      }
    };

    fetchInitialData();
  }, []);

  // Set default date range to last 30 days if no dates are set
  useEffect(() => {
    if (!filters.startDate && !filters.endDate && hasAnyTickets !== null && hasAnyTickets) {
      setDateRange('last30Days');
    }
  }, [hasAnyTickets]);

  // Fetch tickets based on filters
  const fetchTickets = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      
      // Log the current filters
      console.log('Current filters:', filters);
      
      // Handle each filter type specifically
      if (filters.customerId) {
        params.append('customerId', filters.customerId);
      }
      if (filters.jobId) {
        params.append('jobId', filters.jobId);
      }
      if (filters.status) {
        params.append('status', filters.status);
      }
      
      // Handle date filtering with validation
      if (filters.startDate) {
        // Validate start date
        const startDate = new Date(filters.startDate);
        if (isNaN(startDate.getTime())) {
          setError('Invalid start date format. Please use YYYY-MM-DD format.');
          setLoading(false);
          return;
        }
        params.append('startDate', filters.startDate);
      }
      
      if (filters.endDate) {
        // Validate end date
        const endDate = new Date(filters.endDate);
        if (isNaN(endDate.getTime())) {
          setError('Invalid end date format. Please use YYYY-MM-DD format.');
          setLoading(false);
          return;
        }
        params.append('endDate', filters.endDate);
      }
      
      // Validate date range if both dates are provided
      if (filters.startDate && filters.endDate) {
        const startDate = new Date(filters.startDate);
        const endDate = new Date(filters.endDate);
        if (startDate > endDate) {
          setError('Start date cannot be after end date.');
          setLoading(false);
          return;
        }
      }
      
      if (filters.ticketNumber) {
        params.append('ticketNumber', filters.ticketNumber);
      }
      if (filters.machineId) {
        params.append('machineId', filters.machineId);
      }

      // Log the final params
      console.log('API Request URL:', `/tickets/report?${params.toString()}`);

      const response = await api({
        method: 'get',
        url: `/tickets/report?${params}`
      });
      
      // Log the API response
      console.log('Tickets API Response:', response.data);
      
      if (response.data.success) {
        const ticketsData = response.data.data.tickets || response.data.data;
        console.log('Filtered tickets:', ticketsData);
        
        setTickets(ticketsData);
      } else {
        setError(response.data.message || 'Error fetching tickets');
      }
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError(err.response?.data?.message || 'Error fetching tickets');
    } finally {
      setLoading(false);
    }
  };

  // Fetch tickets when filters change
  useEffect(() => {
    fetchTickets();
  }, [filters]);

  // Check if there are any tickets at all (for guidance)
  useEffect(() => {
    const checkForTickets = async () => {
      try {
        const response = await api({
          method: 'get',
          url: '/tickets/report'
        });
        if (response.data.success) {
          const ticketsData = response.data.data.tickets || response.data.data;
          setHasAnyTickets(ticketsData.length > 0);
        }
      } catch (err) {
        console.error('Error checking for tickets:', err);
      }
    };
    
    checkForTickets();
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    
    // Handle special cases
    if (name === 'customerId' || name === 'jobId' || name === 'machineId') {
      processedValue = value === '' ? '' : parseInt(value);
    }
    
    console.log('Setting filter:', name, processedValue);
    setFilters(prev => {
      const next = { ...prev, [name]: processedValue };
      // Clear the selected job if it doesn't belong to the newly selected customer
      if (name === 'customerId' && processedValue && prev.jobId) {
        const selectedJob = jobs.find(j => j.id === prev.jobId);
        if (!selectedJob || selectedJob.customerId !== processedValue) {
          next.jobId = '';
        }
      }
      return next;
    });
  };

  // Only show the selected customer's jobs in the Job dropdown
  const jobOptions = filters.customerId
    ? jobs.filter(j => j.customerId === filters.customerId)
    : jobs;

  const handleResetFilters = () => {
    setFilters({
      customerId: '',
      jobId: '',
      status: '',
      startDate: '',
      endDate: '',
      ticketNumber: '',
      machineId: ''
    });
  };

  // Helper function to set date range for common periods
  const setDateRange = (range) => {
    const today = new Date();
    let startDate = '';
    let endDate = '';
    
    switch (range) {
      case 'today':
        startDate = today.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case 'thisWeek':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        startDate = startOfWeek.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case 'thisMonth':
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        startDate = startOfMonth.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case 'lastMonth':
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        startDate = lastMonth.toISOString().split('T')[0];
        endDate = endOfLastMonth.toISOString().split('T')[0];
        break;
      case 'last30Days':
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);
        startDate = thirtyDaysAgo.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      default:
        return;
    }
    
    setFilters(prev => ({
      ...prev,
      startDate,
      endDate
    }));
  };

  const formatCurrency = (amount) => {
    if (isNaN(amount) || amount === null || amount === undefined) {
      return '$0';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return formatDateForDisplay(dateStr);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Paid':
        return 'success';
      case 'Pending':
        return 'warning';
      case 'Rejected':
        return 'danger';
      case 'Approved':
        return 'info';
      case 'Invoiced':
        return 'info';
      default:
        return 'secondary';
    }
  };

  const getStatusDisplay = (status) => {
    return status || 'Unknown';
  };

  const getStatusDescription = (status) => {
    switch (status) {
      case 'Pending':
        return 'Awaiting approval';
      case 'Approved':
        return 'Ready for payment';
      case 'Invoiced':
        return 'Invoice sent';
      case 'Paid':
        return 'Payment received';
      case 'Rejected':
        return 'Not approved';
      default:
        return '';
    }
  };

  // Calculate summary statistics from displayed ticket line items only (correct when API filters by machine).
  const reportItems = tickets.flatMap((ticket) => ticket.TicketItems || []);
  const summary = {
    totalTickets: tickets.length,
    totalAmount: reportItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0),
    totalHours: sumReportItemsQuantity(reportItems),
    totalHoursDisplay: formatTicketItemsHoursTotal(reportItems),
    totalGallons: reportItems.reduce((sum, item) => sum + (parseFloat(item.gallonsUsed) || 0), 0),
    pendingTickets: tickets.filter(ticket => ticket.status === 'Pending').length,
    approvedTickets: tickets.filter(ticket => ticket.status === 'Approved').length,
    paidTickets: tickets.filter(ticket => ticket.status === 'Paid').length,
    rejectedTickets: tickets.filter(ticket => ticket.status === 'Rejected').length
  };

  const handlePrint = () => {
    // Prepare report data with properly formatted filters
    const reportData = {
      filters: {
        // Only include filters that have values
        ...(filters.customerId && {
          customerId: filters.customerId,
          customerName: customers.find(c => c.id === filters.customerId)?.companyName || 'N/A'
        }),
        ...(filters.jobId && {
          jobId: filters.jobId,
          jobTitle: jobs.find(j => j.id === filters.jobId)?.title || 'N/A'
        }),
        ...(filters.status && {
          status: filters.status
        }),
        ...(filters.startDate && {
          startDate: filters.startDate
        }),
        ...(filters.endDate && {
          endDate: filters.endDate
        }),
        ...(filters.ticketNumber && {
          ticketNumber: filters.ticketNumber
        }),
        ...(filters.machineId && {
          machineId: filters.machineId,
          machineName: machines.find(m => m.id === filters.machineId)?.name || 'N/A'
        })
      },
      summary: {
        ...summary,
        totalGallons: summary.totalGallons
      },
      tickets: tickets.map(ticket => ({
        ...ticket,
        Customer: ticket.Customer || customers.find(c => c.id === ticket.customerId),
        Job: ticket.Job || jobs.find(j => j.id === ticket.jobId),
        TicketItems: ticket.TicketItems || []
      }))
    };

    // Log the data being stored (for debugging)
    console.log('Storing report data:', reportData);

    // Save to localStorage
    localStorage.setItem('ticketReportData', JSON.stringify(reportData));

    // Open print window using the dedicated print view HTML
    const printWindow = window.open('/print-view.html', '_blank');
    printWindow.focus();
  };

  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('GLW Ticket Reports');
  
    // --- TITLE ---
    const titleRow = worksheet.addRow(['', 'GLW Ticket Reports']);
    worksheet.mergeCells(1, 2, 1, 12);
    const titleCell = titleRow.getCell(2);
    titleCell.font = { name: 'Arial', bold: true, size: 24, color: { argb: 'FF8B4513' } };
    titleCell.alignment = { horizontal: 'left', vertical: 'middle' };
    titleRow.height = 35;
  
    worksheet.addRow([]); // blank row
  
    // --- HEADERS ---
    const headerValues = [
      '', 'Ticket Number', 'Customer', 'Job', 'Work Date',
      'Machine', 'Operator', 'Total Hours', 'Rate',
      'TOTAL', 'Description', 'Status'
    ];
    const headerRow = worksheet.addRow(headerValues);
  
    headerRow.font = { name: 'Arial', bold: true, color: { argb: 'FF8B4513' } };
    headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
    headerRow.height = 20;
  
    worksheet.columns = [
      { key: 'empty', width: 5 },
      { key: 'ticketNumber', width: 15 },
      { key: 'customer', width: 20 },
      { key: 'job', width: 20 },
      { key: 'workDate', width: 15 },
      { key: 'machine', width: 15 },
      { key: 'operator', width: 15 },
      { key: 'hours', width: 12 },
      { key: 'rate', width: 12 },
      { key: 'total', width: 15 },
      { key: 'description', width: 25 },
      { key: 'status', width: 12 }
    ];
  
    // Add border to header cells
    headerRow.eachCell(cell => {
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });
  
    // --- DATA ROWS ---
    tickets.forEach(ticket => {
      if (!ticket.TicketItems?.length) {
        const row = worksheet.addRow([
          '',
          ticket.ticketNumber,
          ticket.Customer?.companyName || '',
          ticket.Job?.title || '',
          ticket.workDate ? formatDate(ticket.workDate) : '',
          '',
          '',
          0,
          0,
          0,
          ticket.description || '',
          ticket.status || ''
        ]);
        
        // Explicitly set numeric values and formatting for numeric columns
        const hoursCell = row.getCell(8);
        hoursCell.value = 0;
        hoursCell.numFmt = '0.00'; // Total Hours
        
        const rateCell = row.getCell(9);
        rateCell.value = 0;
        rateCell.numFmt = '0.00'; // Rate
        
        const totalCell = row.getCell(10);
        totalCell.value = 0;
        totalCell.numFmt = '0.00'; // TOTAL
        
        row.eachCell(cell => {
          cell.font = { name: 'Arial' };
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
        });
      } else {
        ticket.TicketItems.forEach(item => {
          const { rate, amount } = getTicketItemRateAndAmount(item);
          const qtyValue = item.rateType === 'daily' ? null
            : item.rateType === 'load' ? parseFloat(item.loadsUsed || 0)
            : parseFloat(item.hoursUsed || 0);
          const rateValue = parseFloat(rate) || 0;
          const amountValue = parseFloat(amount) || 0;
          const gallonsValue = parseFloat(item.gallonsUsed) || 0;
          
          const row = worksheet.addRow([
            '',
            ticket.ticketNumber,
            ticket.Customer?.companyName || '',
            ticket.Job?.title || '',
            ticket.workDate ? formatDate(ticket.workDate) : '',
            item.Machine?.name || '',
            item.Employee?.name || '',
            item.rateType === 'daily' ? 'Daily Rate' : qtyValue,
            rateValue,
            amountValue,
            item.description || ticket.description || '',
            ticket.status || ''
          ]);
          
          // Explicitly set numeric values and formatting for numeric columns
          if (item.rateType !== 'daily') {
            const hoursCell = row.getCell(8);
            hoursCell.value = qtyValue;
            hoursCell.numFmt = '0.00'; // Total Hours
          }
          
          const rateCell = row.getCell(9);
          rateCell.value = rateValue;
          rateCell.numFmt = '0.00'; // Rate
          
          const totalCell = row.getCell(10);
          totalCell.value = amountValue;
          totalCell.numFmt = '0.00'; // TOTAL
          
          row.eachCell(cell => {
            cell.font = { name: 'Arial' };
            cell.border = {
              top: { style: 'thin' },
              left: { style: 'thin' },
              bottom: { style: 'thin' },
              right: { style: 'thin' }
            };
          });
        });
      }
    });
  
    // --- TOTAL ROW ---
    const totalRow = worksheet.addRow([
      '', 'Totals', '', '', '', '', '',
      summary.totalHours,
      '', summary.totalAmount, '', ''
    ]);
    
    // Explicitly set numeric values and formatting for numeric columns in total row 
    const totalHoursCell = totalRow.getCell(8);
    totalHoursCell.value = parseFloat(summary.totalHours) || 0;
    totalHoursCell.numFmt = '0.00'; // Total Hours
    
    const totalAmountCell = totalRow.getCell(10);
    totalAmountCell.value = parseFloat(summary.totalAmount) || 0;
    totalAmountCell.numFmt = '0.00'; // TOTAL
    
    totalRow.eachCell(cell => {
      cell.font = { name: 'Arial', bold: true };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC6E6C6' } };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });
  
    worksheet.addRow([]);
    worksheet.addRow([]);
  
    // --- COMMENTS SECTION ---
    const commentsRow = worksheet.addRow(['', '***Comments***']);
    commentsRow.getCell(2).font = { name: 'Arial', bold: true };
    const commentBox = worksheet.addRow(['', '']);
    commentBox.eachCell(cell => {
      cell.font = { name: 'Arial' };
    });
    worksheet.mergeCells(`B${commentBox.number}:L${commentBox.number + 1}`);
    worksheet.addRow([]);
  
    // --- APPROVAL SECTION ---
    const approvalRow = worksheet.addRow(['', 'Approval', '___________________________']);
    approvalRow.eachCell(cell => {
      cell.font = { name: 'Arial' };
    });
    approvalRow.getCell(2).font = { name: 'Arial', bold: true };
  
    // --- STYLE TWEAKS ---
    worksheet.getColumn(1).alignment = { horizontal: 'left' };
    worksheet.getColumn(2).alignment = { horizontal: 'left' };
  
    // Make column A completely white (background and borders)
    worksheet.eachRow((row, rowNumber) => {
      const cell = row.getCell(1);
      if (cell) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFFFFFFF' } },
          left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
          bottom: { style: 'thin', color: { argb: 'FFFFFFFF' } },
          right: { style: 'thin', color: { argb: 'FFFFFFFF' } }
        };
      }
    });
  
    // --- DOWNLOAD ---
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `GLW_Ticket_Reports_${new Date().toISOString().split('T')[0]}.xlsx`;
    link.click();
    URL.revokeObjectURL(link.href);
  };
  

  const exportToCSV = () => {
    // Helper function to escape CSV values
    const escapeCSV = (value) => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    // Build CSV content - just the table starting from column A
    const csvRows = [];

    // Headers (starting at column A)
    const headers = [
      'Ticket Number', 'Customer', 'Job', 'Work Date',
      'Machine', 'Operator', 'Total Hours', 'Rate',
      'TOTAL', 'Description', 'Status'
    ];
    csvRows.push(headers.map(escapeCSV).join(','));

    // Data rows
    tickets.forEach(ticket => {
      if (!ticket.TicketItems?.length) {
        const row = [
          ticket.ticketNumber,
          ticket.Customer?.companyName || '',
          ticket.Job?.title || '',
          ticket.workDate ? formatDate(ticket.workDate) : '',
          '',
          '',
          0,
          0,
          0,
          ticket.description || '',
          ticket.status || ''
        ];
        csvRows.push(row.map(escapeCSV).join(','));
      } else {
        ticket.TicketItems.forEach(item => {
          const { rate, amount } = getTicketItemRateAndAmount(item);
          const hoursValue = item.rateType === 'daily'
            ? 'Daily Rate'
            : item.rateType === 'load'
              ? parseFloat(item.loadsUsed || 0).toFixed(2)
              : (parseFloat(item.hoursUsed || 0).toFixed(2));
          const rateValue = parseFloat(rate).toFixed(2);
          const amountValue = parseFloat(amount).toFixed(2);
          
          const row = [
            ticket.ticketNumber,
            ticket.Customer?.companyName || '',
            ticket.Job?.title || '',
            ticket.workDate ? formatDate(ticket.workDate) : '',
            item.Machine?.name || '',
            item.Employee?.name || '',
            hoursValue,
            rateValue,
            amountValue,
            item.description || ticket.description || '',
            ticket.status || ''
          ];
          csvRows.push(row.map(escapeCSV).join(','));
        });
      }
    });

    // Total row
    const totalRow = [
      'Totals', '', '', '', '', '',
      parseFloat(summary.totalHours || 0).toFixed(2),
      '',
      parseFloat(summary.totalAmount || 0).toFixed(2),
      '', ''
    ];
    csvRows.push(totalRow.map(escapeCSV).join(','));

    // Convert to CSV string
    const csvContent = csvRows.join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Tickets_Report_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (userLoading) {
    return (
      <div style={sharedStyles.pageContainer} className="container-fluid p-4">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  if (user?.role === 'foreman') {
    return (
      <div style={sharedStyles.pageContainer} className="container-fluid p-4">
        <div className="alert alert-warning mb-0">Ticket reports are not available for your role.</div>
      </div>
    );
  }

  return (
    <div style={sharedStyles.pageContainer} className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 style={sharedStyles.pageTitle}>Tickets Report</h2>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-success"
            onClick={exportToExcel}
          >
            <i className="bi bi-file-earmark-excel me-2"></i>
            Export to Excel
          </button>
          <button
            className="btn btn-outline-secondary"
            onClick={exportToCSV}
          >
            <i className="bi bi-file-earmark-text me-2"></i>
            Export to CSV
          </button>
          <button
            className="btn btn-outline-primary"
            onClick={handlePrint}
          >
            <i className="bi bi-printer me-2"></i>
            Print Report
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Filters</h5>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-2">
              <label className="form-label">Ticket Number</label>
              <input
                type="text"
                className="form-control"
                name="ticketNumber"
                value={filters.ticketNumber}
                onChange={handleFilterChange}
                placeholder="Search ticket number..."
              />
            </div>
            <div className="col-md-2">
              <SearchableDropdown
                label="Customer"
                name="customerId"
                value={filters.customerId || ''}
                onChange={handleFilterChange}
                options={customers}
                placeholder="All Customers"
                emptyMessage="No customers found"
                displayKey="companyName"
                valueKey="id"
                searchKeys={['companyName', 'contactPerson']}
                maxHeight="250px"
              />
            </div>
            <div className="col-md-2">
              <SearchableDropdown
                label="Job Name"
                name="jobId"
                value={filters.jobId || ''}
                onChange={handleFilterChange}
                options={jobOptions}
                placeholder="All Jobs"
                emptyMessage="No jobs found"
                displayKey="title"
                valueKey="id"
                searchKeys={['title']}
                showCustomer={true}
                maxHeight="250px"
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                name="status"
                value={filters.status || ''}
                onChange={handleFilterChange}
              >
                <option value="">All Statuses</option>
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Invoiced">Invoiced</option>
                <option value="Paid">Paid</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
            <div className="col-md-2">
              <SearchableDropdown
                label="Machine"
                name="machineId"
                value={filters.machineId || ''}
                onChange={handleFilterChange}
                options={machines.sort((a, b) => a.name.localeCompare(b.name))}
                placeholder="All Machines"
                emptyMessage="No machines found"
                displayKey="name"
                valueKey="id"
                searchKeys={['name', 'model', 'serialNumber']}
                maxHeight="250px"
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Created From</label>
              <input
                type="date"
                className="form-control"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
                title="Filter by ticket creation date"
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Created To</label>
              <input
                type="date"
                className="form-control"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
                title="Filter by ticket creation date"
              />
            </div>
            <div className="col-12">
              <div className="d-flex gap-2 mb-2">
                <small className="text-muted me-2">Quick Date Ranges:</small>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setDateRange('today')}
                >
                  Today
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setDateRange('thisWeek')}
                >
                  This Week
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setDateRange('thisMonth')}
                >
                  This Month
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setDateRange('lastMonth')}
                >
                  Last Month
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setDateRange('last30Days')}
                >
                  Last 30 Days
                </button>
              </div>
            </div>
            <div className="col-12">
              <div className="d-flex gap-2">
                <button
                  className="btn btn-secondary"
                  onClick={handleResetFilters}
                >
                  <i className="bi bi-x-circle me-2"></i>
                  Reset Filters
                </button>
                <button
                  className="btn btn-primary"
                  onClick={fetchTickets}
                >
                  <i className="bi bi-search me-2"></i>
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Card */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row">
            <div className="col-md-2">
              <div className="border rounded p-3">
                <h6 className="text-muted mb-2">Total Tickets</h6>
                <h3 className="mb-0">{summary.totalTickets}</h3>
              </div>
            </div>
            <div className="col-md-2">
              <div className="border rounded p-3">
                <h6 className="text-muted mb-2">Total Amount</h6>
                <h3 className="mb-0">{formatCurrency(summary.totalAmount)}</h3>
              </div>
            </div>
            <div className="col-md-2">
              <div className="border rounded p-3">
                <h6 className="text-muted mb-2">Total Hours</h6>
                <h3 className="mb-0">{summary.totalHoursDisplay}</h3>
              </div>
            </div>
            <div className="col-md-2">
              <div className="border rounded p-3">
                <h6 className="text-muted mb-2">Total Gallons</h6>
                <h3 className="mb-0">{summary.totalGallons.toFixed(2)}</h3>
              </div>
            </div>
            <div className="col-md-2">
              <div className="border rounded p-3">
                <h6 className="text-muted mb-2">Pending Tickets</h6>
                <h3 className="mb-0">{summary.pendingTickets}</h3>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}

      {/* Warning for future dates */}
      {filters.startDate && new Date(filters.startDate) > new Date() && (
        <div className="alert alert-warning alert-dismissible fade show" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          You've set a start date in the future ({formatDate(filters.startDate)}). This will likely return no results. 
          Consider using a past date range or clicking one of the quick date range buttons above.
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}

      {/* Tickets Table */}
      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover align-middle" style={sharedStyles.table}>
              <thead className="table-light">
                <tr>
                  <th>Ticket Number</th>
                  <th>Customer</th>
                  <th>Job Name</th>
                  <th>Work Date</th>
                  <th>Creator</th>
                  <th>Machines</th>
                  <th>Total Hours</th>
                  <th>Gallons Used</th>
                  <th>Total Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : tickets.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center py-4 text-muted">
                      <i className="bi bi-receipt mb-2" style={{ fontSize: '1.5rem' }}></i>
                      <p className="mb-0">No tickets found</p>
                      <small className="text-muted">
                        {hasAnyTickets === false ? (
                          <span>No tickets exist in the system yet. Create some tickets first.</span>
                        ) : (
                          <span>
                            Try adjusting your filters or date range. 
                            {filters.startDate && filters.endDate && (
                              <span> Current date range: {formatDate(filters.startDate)} to {formatDate(filters.endDate)}</span>
                            )}
                            {filters.startDate && new Date(filters.startDate) > new Date() && (
                              <span className="d-block text-warning mt-1">
                                <i className="bi bi-exclamation-triangle"></i> 
                                Start date is in the future. Try using a past date range.
                              </span>
                            )}
                          </span>
                        )}
                      </small>
                    </td>
                  </tr>
                ) : (
                  tickets.map(ticket => (
                    <tr key={ticket.id}>
                      <td>
                        <div className="fw-medium">{ticket.ticketNumber}</div>
                        <small className="text-muted">Created: {formatDate(ticket.createdAt)}</small>
                      </td>
                      <td>
                        <div className="fw-medium">{ticket.Customer?.companyName}</div>
                      </td>
                      <td>
                        <div className="fw-medium">{ticket.Job?.title}</div>
                      </td>
                      <td>
                        <div className="d-flex flex-column">
                          <span>{ticket.workDate ? formatDate(ticket.workDate) : 'N/A'}</span>
                        </div>
                      </td>
                      <td>
                        <div className="fw-medium">{ticket.createdByName || 'N/A'}</div>
                      </td>
                      <td>
                        <div className="d-flex flex-column">
                          {ticket.TicketItems?.map((item, index) => (
                            <div key={index} className="mb-1">
                              <span className="fw-medium">{item.Machine?.name || 'N/A'}</span>
                              <small className="text-muted d-block">
                                {formatHours(item.rateType === 'load' ? item.loadsUsed : item.hoursUsed, item.rateType)}
                                {item.Employee && (
                                  <span className="text-muted"> (Op: {item.Employee.name})</span>
                                )}
                                {item.gallonsUsed && parseFloat(item.gallonsUsed) > 0 && (
                                  <span className="text-muted d-block">
                                    <i className="bi bi-fuel-pump me-1"></i>
                                    {parseFloat(item.gallonsUsed).toFixed(2)} gal
                                  </span>
                                )}
                              </small>
                            </div>
                          )) || 'N/A'}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <span className="badge bg-info me-2">
                            <i className="bi bi-clock"></i>
                          </span>
                          <span className="fw-medium">{formatTicketItemsHoursTotal(ticket.TicketItems)}</span>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <span className="badge bg-success me-2">
                            <i className="bi bi-fuel-pump"></i>
                          </span>
                          <span className="fw-medium">
                            {(() => {
                              const g = sumTicketItemsGallons(ticket.TicketItems);
                              return g > 0 ? g.toFixed(2) : '0.00';
                            })()}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="fw-medium">{formatCurrency(sumTicketItemsAmount(ticket.TicketItems))}</div>
                      </td>
                      <td>
                        <div className="d-flex flex-column">
                          <span className={`badge bg-${getStatusBadgeClass(ticket.status)}`}>
                            {getStatusDisplay(ticket.status)}
                          </span>
                          {getStatusDescription(ticket.status) && (
                            <small className="text-muted mt-1">
                              {getStatusDescription(ticket.status)}
                            </small>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {tickets.length > 0 && (
                <tfoot className="table-light">
                  <tr>
                    <td colSpan="7" className="text-end fw-bold">Total:</td>
                    <td className="fw-bold">{summary.totalHoursDisplay}</td>
                    <td className="fw-bold">{summary.totalGallons.toFixed(2)} gal</td>
                    <td className="fw-bold">{formatCurrency(summary.totalAmount)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketReports;